import {Dispatch, SetStateAction, useMemo, useState} from 'react';
import {Box, Chip, Divider, Skeleton, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {CarePlanConfigurationFeature} from '../../../backend/enums/care-plan-configuration-feature';
import {EntityActivitiesRepository} from '../../../repositories/entity-activities';
import {
  ConfigurationsIndex,
  PeriodsOfTheDaySchedule,
  RepetitionSchedule,
  SosSchedule,
  TimeListSchedule,
} from '../../../types';
import {CarePlanActivitiesGroupApiRequestConfigurationsItem} from '../../../backend/requests/care-plan-activities-group';
import {convertArrayToIndex, decomposedPosologyScheduleExpression} from '../../../utils/functions';
import {ActivityType} from '../../../backend/interfaces/activity-type';
import {CarePlanConfigurationType} from '../../../backend/enums/care-plan-configuration-type';
import {PosologySchedule} from '../../../backend/enums/posology-schedule';

interface MedicationCardContentProps {
  entityId: string;
  activitiesSectorId: string;
  activitiesGroupId: string;
  activityType: ActivityType;
  configurationsIndex: ConfigurationsIndex | undefined;
  valuesIndex: {[key: string]: CarePlanActivitiesGroupApiRequestConfigurationsItem};
  periodsOfTheDay: ActivityType[] | undefined;
}

const MedicationCardContent = ({
  entityId,
  activitiesSectorId,
  activitiesGroupId,
  activityType,
  configurationsIndex,
  valuesIndex,
  periodsOfTheDay,
}: MedicationCardContentProps) => {
  const {t} = useTranslation();

  const [medicineItem, setMedicineItem] = useState<string | undefined>(undefined);
  const [medicineDosageUnitItem, setMedicineDosageUnitItem] = useState<string | undefined>(
    undefined
  );
  const [medicineAdministrationRouteItem, setMedicineAdministrationRouteItem] = useState<
    string | undefined
  >(undefined);
  const fetchCarePlanConfigurationItemById = async (
    carePlanConfigurationId: string,
    carePlanConfigurationItemId: string,
    setStateFunc: Dispatch<SetStateAction<string | undefined>>
  ) => {
    const item = await EntityActivitiesRepository.instance.getGroupCarePlanConfigurationDataValue(
      entityId,
      activitiesSectorId,
      activitiesGroupId,
      activityType.id!,
      carePlanConfigurationId,
      carePlanConfigurationItemId
    );

    setStateFunc(item.name);
  };

  // Meal periods of the day, indexed by id
  const indexedPeriodsOfTheDay: {[key: string]: ActivityType} = useMemo(() => {
    if (periodsOfTheDay) {
      return convertArrayToIndex(periodsOfTheDay, 'id');
    }
    return {};
  }, [periodsOfTheDay]);

  const medicationValuesIndexedByFeature = useMemo(() => {
    const result: {[key in CarePlanConfigurationFeature]?: string} = {};

    if (configurationsIndex && valuesIndex) {
      const featureConfigurations = Object.values(configurationsIndex).filter(
        (configuration) => !!configuration.feature
      );

      // Add defaults
      const medicineFeatureConfigurationsIndex = featureConfigurations.findIndex(
        (configuration) => configuration.feature === CarePlanConfigurationFeature.Medicine
      );
      if (medicineFeatureConfigurationsIndex === -1) {
        featureConfigurations.push({
          id: 'default-medicine',
          name: 'default-medicine',
          feature: CarePlanConfigurationFeature.Medicine,
          type: CarePlanConfigurationType.String,
        });
      }
      const medicineAdministrationRouteFeatureConfigurationsIndex = featureConfigurations.findIndex(
        (configuration) =>
          configuration.feature === CarePlanConfigurationFeature.MedicineAdministrationRoute
      );
      if (medicineAdministrationRouteFeatureConfigurationsIndex === -1) {
        featureConfigurations.push({
          id: 'default-medicine-administration-route',
          name: 'default-medicine-administration-route',
          feature: CarePlanConfigurationFeature.MedicineAdministrationRoute,
          type: CarePlanConfigurationType.String,
        });
      }
      const medicineDosageUnitFeatureConfigurationsIndex = featureConfigurations.findIndex(
        (configuration) => configuration.feature === CarePlanConfigurationFeature.MedicineDosageUnit
      );
      if (medicineDosageUnitFeatureConfigurationsIndex === -1) {
        featureConfigurations.push({
          id: 'default-medicine-dosage-unit',
          name: 'default-medicine-dosage-unit',
          feature: CarePlanConfigurationFeature.MedicineDosageUnit,
          type: CarePlanConfigurationType.String,
        });
      }

      featureConfigurations.forEach((configuration) => {
        result[configuration.feature!] = valuesIndex[configuration.id!]?.carePlanConfigurationValue;
        if (!valuesIndex[configuration.id]) {
          return;
        }

        // Async load of fields where the value is the id of the item that holds the name to display in the BE
        if (configuration.feature === CarePlanConfigurationFeature.Medicine && !medicineItem) {
          if (!valuesIndex[configuration.id!]!.carePlanConfigurationValue) {
            setMedicineItem('---');
            return;
          }
          fetchCarePlanConfigurationItemById(
            configuration.id!,
            valuesIndex[configuration.id!]!.carePlanConfigurationValue!,
            setMedicineItem
          );
        } else if (
          configuration.feature === CarePlanConfigurationFeature.MedicineAdministrationRoute &&
          !medicineAdministrationRouteItem
        ) {
          if (!valuesIndex[configuration.id!]!.carePlanConfigurationValue) {
            setMedicineAdministrationRouteItem('---');
            return;
          }
          fetchCarePlanConfigurationItemById(
            configuration.id!,
            valuesIndex[configuration.id!]!.carePlanConfigurationValue!,
            setMedicineAdministrationRouteItem
          );
        } else if (
          configuration.feature === CarePlanConfigurationFeature.MedicineDosageUnit &&
          !medicineDosageUnitItem
        ) {
          if (!valuesIndex[configuration.id!]!.carePlanConfigurationValue) {
            setMedicineDosageUnitItem('---');
            return;
          }
          // Dosage unit values are sent in arrayItems, find there the name to display
          const dosageUnitValue = configuration.arrayItems?.find(
            (item) => item.id === valuesIndex[configuration.id!]!.carePlanConfigurationValue!
          );
          if (dosageUnitValue && dosageUnitValue.name) {
            setMedicineDosageUnitItem(dosageUnitValue.name);
          } else {
            setMedicineDosageUnitItem(undefined); // or handle as needed
          }
        }
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurationsIndex, valuesIndex]);

  const scheduleDefinition:
    | TimeListSchedule
    | PeriodsOfTheDaySchedule
    | RepetitionSchedule
    | SosSchedule
    | undefined = useMemo(() => {
    const scheduleContent = medicationValuesIndexedByFeature[CarePlanConfigurationFeature.Schedule];

    if (scheduleContent) {
      return JSON.parse(scheduleContent);
    }
  }, [medicationValuesIndexedByFeature]);

  // Is SOS Schedule
  const isSosSchedule =
    scheduleDefinition &&
    (scheduleDefinition as SosSchedule).posologyScheduleType === PosologySchedule.SOS;

  const decomposedPosologySchedule = useMemo(() => {
    if (scheduleDefinition) {
      return decomposedPosologyScheduleExpression({
        t,
        scheduleDefinition,
        periodsOfTheDayMap: indexedPeriodsOfTheDay,
      });
    }
  }, [scheduleDefinition, t, indexedPeriodsOfTheDay]);

  return (
    <Stack className="medication-card-content">
      <Typography className="name">
        {medicationValuesIndexedByFeature[CarePlanConfigurationFeature.Name] || activityType.name}
      </Typography>
      <Stack className="medicine-administration-container">
        {medicineItem ? (
          <Typography className="medicine">{medicineItem}</Typography>
        ) : (
          <Skeleton
            {...{animation: 'wave', variant: 'rounded'}}
            sx={{width: '480px', height: '15px', marginRight: '56px'}}
          />
        )}
        <Stack className="medicine-administration-details">
          {medicineDosageUnitItem ? (
            <Typography className="medicine-dosage">
              {medicationValuesIndexedByFeature[CarePlanConfigurationFeature.MedicineDosage]}{' '}
              {medicineDosageUnitItem}
            </Typography>
          ) : (
            <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={100} height={15} />
          )}
          {medicineAdministrationRouteItem ? (
            <Typography className="medicine-administration-route">
              {medicineAdministrationRouteItem}
            </Typography>
          ) : (
            <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={180} height={15} />
          )}

          {decomposedPosologySchedule ? (
            <>
              {isSosSchedule && (
                <Typography className="medicine-administration-schedule" noWrap>
                  ---
                </Typography>
              )}
              {!isSosSchedule && (
                <Typography className="medicine-administration-schedule" noWrap>
                  {decomposedPosologySchedule.readableFrequency}
                </Typography>
              )}
            </>
          ) : (
            <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={300} height={15} />
          )}
        </Stack>
      </Stack>
      <Divider className="medication-divider" />
      <Stack className="medication-schedule-container">
        {decomposedPosologySchedule ? (
          <Stack className="schedule-content">
            {decomposedPosologySchedule.readableQuantities?.map((readableQuantity, index) => (
              <Typography className="schedule-item" key={index}>
                {readableQuantity}
              </Typography>
            ))}
          </Stack>
        ) : (
          <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={300} height={15} />
        )}

        {decomposedPosologySchedule ? (
          <Typography className="schedule-end">
            {decomposedPosologySchedule.readableEndDateTime}
          </Typography>
        ) : (
          <Skeleton {...{animation: 'wave', variant: 'rounded'}} width={180} height={15} />
        )}
      </Stack>
      <Box className="medication-chips-container">
        {medicationValuesIndexedByFeature[CarePlanConfigurationFeature.MedicineBox] && (
          <Chip className="box-chip" label="BOX" />
        )}
        {medicationValuesIndexedByFeature[CarePlanConfigurationFeature.MedicineSOS] && (
          <Chip className="sos-chip" label="SOS" />
        )}
      </Box>
    </Stack>
  );
};

export default MedicationCardContent;
